import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  PartyActivityCatalogItemSchema,
  PartyBookingListResponseSchema,
  PartyBookingSchema,
  WeekendSlotSummaryResponseSchema,
  type CreatePartyInput,
  type PartyActivityCatalogItem,
  type PartyAddonRequestInput,
  type PartyBooking,
  type PartyFormat,
} from "@/api/types/party";
import { z } from "zod";
import { InvoiceSchema, type Invoice } from "@/api/types/invoice";

/** Query-key factory — mirrors src/api/hooks/useBookings.ts's convention. */
export const partyKeys = {
  all: ["parties"] as const,
  weekends: (dateFrom: string, dateTo: string) => [...partyKeys.all, "weekends", dateFrom, dateTo] as const,
  lists: () => [...partyKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) => [...partyKeys.lists(), params] as const,
  details: () => [...partyKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...partyKeys.details(), id] as const,
  activities: (format?: PartyFormat) => [...partyKeys.all, "activities", format ?? "all"] as const,
};

/** Zone A — staff catalog listing (GET /v2/parties/activities), backing
 * CreatePartyDialog.tsx's format/selection picker. */
export function usePartyActivities(format?: PartyFormat) {
  return useQuery<PartyActivityCatalogItem[]>({
    queryKey: partyKeys.activities(format),
    queryFn: async () => {
      const searchParams = format ? `?format=${format}` : "";
      const { data } = await apiClient.get(`/v2/parties/activities${searchParams}`);
      return validateResponse(z.array(PartyActivityCatalogItemSchema), data, "GET /v2/parties/activities");
    },
    staleTime: 5 * 60 * 1000,
  });
}

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: partyKeys.all });
}

export function useWeekendSlotSummary(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: partyKeys.weekends(dateFrom, dateTo),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/parties/weekends?date_from=${dateFrom}&date_to=${dateTo}`);
      return validateResponse(WeekendSlotSummaryResponseSchema, data, "GET /v2/parties/weekends").items;
    },
    enabled: !!dateFrom && !!dateTo,
    staleTime: 10 * 1000,
  });
}

interface UsePartiesParams {
  status?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export function useParties(params: UsePartiesParams = {}) {
  const { status, date_from, date_to, limit = 50 } = params;
  return useQuery({
    queryKey: partyKeys.list({ status, date_from, date_to, limit }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (status) searchParams.set("status", status);
      if (date_from) searchParams.set("date_from", date_from);
      if (date_to) searchParams.set("date_to", date_to);
      searchParams.set("limit", String(limit));
      const { data } = await apiClient.get(`/v2/parties?${searchParams.toString()}`);
      return validateResponse(PartyBookingListResponseSchema, data, "GET /v2/parties").items;
    },
    staleTime: 10 * 1000,
  });
}

export function useParty(partyId: string | undefined) {
  return useQuery<PartyBooking>({
    queryKey: partyKeys.detail(partyId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/parties/${partyId}`);
      return validateResponse(PartyBookingSchema, data, "GET /v2/parties/:id");
    },
    enabled: !!partyId,
    staleTime: 5 * 1000,
  });
}

export function useCreateParty() {
  const queryClient = useQueryClient();
  return useMutation<PartyBooking, unknown, CreatePartyInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/parties", input);
      return validateResponse(PartyBookingSchema, data, "POST /v2/parties");
    },
    onSuccess: (party) => {
      queryClient.setQueryData(partyKeys.detail(party.id), party);
      invalidateAll(queryClient);
    },
  });
}

export function useAddPartyAddon() {
  const queryClient = useQueryClient();
  return useMutation<PartyBooking, unknown, { partyId: string } & PartyAddonRequestInput>({
    mutationFn: async ({ partyId, ...addon }) => {
      const { data } = await apiClient.post(`/v2/parties/${partyId}/addons`, addon);
      return validateResponse(PartyBookingSchema, data, "POST /v2/parties/:id/addons");
    },
    onSuccess: (party) => {
      queryClient.setQueryData(partyKeys.detail(party.id), party);
      invalidateAll(queryClient);
    },
  });
}

function usePartyAction(endpoint: string, label: string) {
  const queryClient = useQueryClient();
  return useMutation<PartyBooking, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post(`/v2/parties/${id}/${endpoint}`, {});
      return validateResponse(PartyBookingSchema, data, `POST /v2/parties/:id/${label}`);
    },
    onSuccess: (party) => {
      queryClient.setQueryData(partyKeys.detail(party.id), party);
      invalidateAll(queryClient);
    },
  });
}

export function useConfirmParty() {
  return usePartyAction("confirm", "confirm");
}

export function useCancelParty() {
  return usePartyAction("cancel", "cancel");
}

export function useCompleteParty() {
  return usePartyAction("complete", "complete");
}

export function useGeneratePartyInvoice() {
  const queryClient = useQueryClient();
  return useMutation<Invoice, unknown, string>({
    mutationFn: async (partyId) => {
      const { data } = await apiClient.post(`/v2/parties/${partyId}/generate-invoice`, {});
      return validateResponse(InvoiceSchema, data, "POST /v2/parties/:id/generate-invoice");
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}

export function useCreatePartyPortalLink() {
  return useMutation<string, unknown, string>({
    mutationFn: async (partyId) => {
      const { data } = await apiClient.post(`/v2/parties/${partyId}/portal-link`, {});
      return (data as { portal_url: string }).portal_url;
    },
  });
}
